import { useState } from "react";
import type { SourceBulkAction, SourceRow } from "../../contracts/index";

export function canSelectSource(row: SourceRow): boolean {
  return row.health.state !== "not_collected";
}

// Mirrors the server's availability rules so the bar can say how many sources each action applies to; the preview
// re-checks every source and lists the skipped ones with the reason.
export function bulkApplies(action: SourceBulkAction, row: SourceRow): boolean {
  const state = row.health.state;
  switch (action) {
    case "scan":
      return (
        state !== "paused" &&
        state !== "not_collected" &&
        state !== "not_in_any_plan" &&
        row.next_scan.kind !== "scanning" &&
        row.next_scan.kind !== "none"
      );
    case "pause":
      return state !== "paused" && state !== "not_collected";
    case "resume":
      return state === "paused" && row.pause?.scope === "source";
  }
}

export function bulkCounts(rows: readonly SourceRow[], picked: ReadonlySet<string>): Record<SourceBulkAction, number> {
  const chosen = rows.filter((r) => picked.has(r.id));
  const count = (action: SourceBulkAction) => chosen.filter((r) => bulkApplies(action, r)).length;
  return { scan: count("scan"), pause: count("pause"), resume: count("resume") };
}

export function useSourceSelection(rows: readonly SourceRow[]) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());
  const selectable = rows.filter(canSelectSource);
  const allLoaded = selectable.length > 0 && selectable.every((r) => picked.has(r.id));
  const clear = () => {
    setPicked(new Set());
  };
  const toggle = (row: SourceRow) => {
    const next = new Set(picked);
    if (next.has(row.id)) next.delete(row.id);
    else next.add(row.id);
    setPicked(next);
  };
  const toggleLoaded = () => {
    setPicked(allLoaded ? new Set() : new Set([...picked, ...selectable.map((r) => r.id)]));
  };
  return { picked, toggle, toggleLoaded, clear };
}

export type SourceSelection = ReturnType<typeof useSourceSelection>;
